import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import Modal from 'app/components/Modal/Modal';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {recordEvent} from 'app/tools/Analytics';
import {formatFeatureName} from 'app/utils/featureUtils';
import {isMultiLocationProperty} from 'app/utils/multiFeaturePropertyUtils';
import {makeUuidPrefix} from 'app/utils/routeUtils';

const NAME_PATH = ['properties', 'name'] as any;
const PART_NAME_PATH = ['properties', 'multiFeaturePartName'] as any;
const LENS_ID_PATH = ['properties', 'lensId'] as any;

function makeUngroupedName(
  feature: I.ImmutableOf<ApiFeature>,
  selectedFeatures: I.ImmutableOf<ApiFeature[]>
) {
  const propertyName = ((feature.getIn(NAME_PATH) as string) || '').trim();
  const isMultiLocation = isMultiLocationProperty(selectedFeatures, feature);

  // A user may also have non multi-location properties selected,
  // don't modify the name in this case
  if (!isMultiLocation) {
    return propertyName;
  }

  const partName = ((feature.getIn(PART_NAME_PATH) as string) || '').trim();
  const lensId = ((feature.getIn(LENS_ID_PATH) as string) || '').trim();

  // If there's no part name, use the first 8 characters of lens ID to keep property names unique
  const shortId = makeUuidPrefix(lensId);
  return `${propertyName} - ${partName || shortId}`;
}

export const UngroupLocationsModal: React.FunctionComponent<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClose: () => void;
    onDone: () => void;
    featureCollectionId: number;
    selectedFeatures: I.ImmutableOf<ApiFeature[]>;
    updateFeature: FeaturesActions['updateFeature'];
    setIsUpdatingFeatures: React.Dispatch<React.SetStateAction<boolean>>;
  }>
> = ({
  isOpen,
  onClose,
  onDone,
  featureCollectionId,
  selectedFeatures,
  updateFeature,
  setIsUpdatingFeatures,
}) => {
  const [error, setError] = React.useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const onSubmit = async () => {
    setError(null);
    setIsUpdatingFeatures(true);
    try {
      await Promise.all(
        selectedFeatures
          .filter((f) => !!f)
          .map(async (feature) => {
            const newName = makeUngroupedName(feature!, selectedFeatures);
            await updateFeature(
              feature!.get('id'),
              I.fromJS({properties: {name: newName, multiFeaturePartName: ''}})
            );
          })
          .toArray()
      );

      recordEvent('Split multi-location property', {
        featureCollectionId,
        featureCount: selectedFeatures.size,
      });

      onDone();
      handleClose();
    } catch (e) {
      setError((e as Error)?.message || 'Failed to ungroup locations. Please try again.');
    } finally {
      setIsUpdatingFeatures(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      headerText="Split multi-location property"
      primaryButton={{
        text: 'Split',
        intent: B.Intent.DANGER,
        isDisabled: selectedFeatures.size === 0,
        onClick: onSubmit,
      }}
    >
      <div>
        <B.Callout intent={B.Intent.PRIMARY} icon={null}>
          <p>
            Please confirm you want to split the following{' '}
            <a href="https://lens.tech/support/article/31-multi-location-properties">
              multi-location property
            </a>{' '}
            locations into individual properties:
          </p>
          <ul style={{listStyleType: 'unset', paddingLeft: '2rem'}}>
            {selectedFeatures
              .filter((f) => !!f)
              .toArray()
              .map((f) => (
                <li key={String(f!.get('id'))}>{formatFeatureName(f!.toJS())}</li>
              ))}
          </ul>
        </B.Callout>

        {error && <B.Callout intent={B.Intent.DANGER}>{error}</B.Callout>}
      </div>
    </Modal>
  );
};
